import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { RootState } from "./store";
import { EventCategoryService } from "../services/eventCategoryService";
import { IEventCategory } from "../interfaces/IEventCategory";
import { IEventSubCategory } from "../interfaces/IEventSubCategory";

const selectEventCategoriesState = (state: RootState) => state.eventCategories;
// Selectors
export const selectCategories = (state: RootState) => state.eventCategories.categories;
export const selectSubCategories = (state: RootState) => state.eventCategories.subCategories;
export const selectLoadingCategories = (state: RootState) => state.eventCategories.loadingCategories;
export const selectLoadingSubCategories = (state: RootState) => state.eventCategories.loadingSubCategories;

// Memoized selector for subcategories by categoryId
export const selectSubCategoriesByCategoryId = (categoryId: string) =>
    createSelector(
        [selectEventCategoriesState],
        (eventCategories) => eventCategories.subCategories[categoryId] || []
    );

// Async Thunks
export const fetchCategories = createAsyncThunk<IEventCategory[], AxiosInstance, { state: RootState }>(
    "eventCategories/fetchCategories",
    async (apiClient, { getState }) => {
        const { categories, lastFetched } = getState().eventCategories;
        if (categories.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000) {
            return categories;
        }
        const categoryService = new EventCategoryService(apiClient);
        return await categoryService.getAll();
    }
);

export const fetchSubCategories = createAsyncThunk<
    { categoryId: string; subCategories: IEventSubCategory[] },
    { categoryId: string; apiClient: AxiosInstance },
    { state: RootState }
>(
    "eventCategories/fetchSubCategories",
    async ({ categoryId, apiClient }, { getState }) => {
        const { subCategories } = getState().eventCategories;

        if (subCategories[categoryId]) {
            return { categoryId, subCategories: subCategories[categoryId] };
        }

        const categoryService = new EventCategoryService(apiClient);
        const fetchedSubCategories = await categoryService.getSubCategories(categoryId);

        return { categoryId, subCategories: fetchedSubCategories };
    }
);

// Slice
const eventCategoriesSlice = createSlice({
    name: "eventCategories",
    initialState: {
        categories: [] as IEventCategory[],
        subCategories: {} as Record<string, IEventSubCategory[]>,
        loadingCategories: false, 
        loadingSubCategories: false,
        lastFetched: 0
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.loadingCategories = true;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loadingCategories = false;
                state.categories = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(fetchSubCategories.pending, (state) => {
                state.loadingSubCategories = true;
            })
            .addCase(fetchSubCategories.fulfilled, (state, action) => {
                state.loadingSubCategories = false;

                const { categoryId, subCategories } = action.payload;
                state.subCategories[categoryId] = subCategories;
            });
    }
});

export default eventCategoriesSlice.reducer;